import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// In-memory cache shared across all instances
const imageCache = {};

// Clear old cache entries on new trip view
export const clearImageCache = () => {
  Object.keys(imageCache).forEach(key => delete imageCache[key]);
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith('img_') || key.startsWith('wiki_img_'))) keysToRemove.push(key);
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
};

// Stock transport images by category - used as last-resort fallback
const TRANSPORT_STOCK_IMAGES = {
  taxi: [
    'https://images.unsplash.com/photo-1774271074345-c37f3a3dde15?w=600&h=400&fit=crop',
    'https://images.pexels.com/photos/11171626/pexels-photo-11171626.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
  ],
  rideshare: [
    'https://images.unsplash.com/photo-1774271074345-c37f3a3dde15?w=600&h=400&fit=crop',
    'https://images.pexels.com/photos/19931918/pexels-photo-19931918.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
  ],
  flight: [
    'https://images.unsplash.com/photo-1767868278136-e00fc978e2ae?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1758837573876-63871cc70fd6?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1767868277616-bc240e7bbd57?w=600&h=400&fit=crop',
  ],
  light_rail: [
    'https://images.unsplash.com/photo-1762380450665-5258fc76139e?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1727086782355-c6cd1c5a204f?w=600&h=400&fit=crop',
  ],
  tram: [
    'https://images.unsplash.com/photo-1762380450665-5258fc76139e?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1727086782355-c6cd1c5a204f?w=600&h=400&fit=crop',
  ],
  bus: [
    'https://images.unsplash.com/photo-1760488604779-ae77ea1d60ce?w=600&h=400&fit=crop',
    'https://images.pexels.com/photos/18989536/pexels-photo-18989536.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
  ],
  ferry: [
    'https://images.unsplash.com/photo-1631365479301-8601e9eebf05?w=600&h=400&fit=crop',
    'https://images.pexels.com/photos/32392560/pexels-photo-32392560.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
  ],
  train: [
    'https://images.unsplash.com/photo-1751206319357-3cb6c6ca578e?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1592963666096-cb44037d0c7a?w=600&h=400&fit=crop',
  ],
  bicycle: [
    'https://images.unsplash.com/photo-1765863238009-2dcb191da188?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1761486554839-8033e31739b0?w=600&h=400&fit=crop',
  ],
  car: [
    'https://images.unsplash.com/photo-1762788123041-a20fe89e4a5f?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1587580945215-5d4aabb2c8ef?w=600&h=400&fit=crop',
  ],
  card: [
    'https://images.unsplash.com/photo-1688149013444-da644d290749?w=600&h=400&fit=crop',
    'https://images.pexels.com/photos/8555255/pexels-photo-8555255.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
  ],
  walking: [
    'https://images.unsplash.com/photo-1760995822332-0f95b74ea182?w=600&h=400&fit=crop',
    'https://images.pexels.com/photos/6126480/pexels-photo-6126480.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
  ],
  shuttle: [
    'https://images.unsplash.com/photo-1764776401208-18c289c9c80c?w=600&h=400&fit=crop',
    'https://images.pexels.com/photos/15511266/pexels-photo-15511266.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
  ],
  metro: [
    'https://images.unsplash.com/photo-1762380450665-5258fc76139e?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1727086782355-c6cd1c5a204f?w=600&h=400&fit=crop',
  ],
  cable_car: [
    'https://images.unsplash.com/photo-1581347166733-7485d118b215?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1741004420761-8a7f489476c6?w=600&h=400&fit=crop',
  ],
  scooter: [
    'https://images.unsplash.com/photo-1745487383221-45fee0110610?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1631994300119-2a17928106b6?w=600&h=400&fit=crop',
  ],
  airport: [
    'https://images.unsplash.com/photo-1767868278136-e00fc978e2ae?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1758837573876-63871cc70fd6?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1767868277616-bc240e7bbd57?w=600&h=400&fit=crop',
  ],
};

// Map transport keywords to stock image categories
const getTransportCategory = (query) => {
  const q = (query || '').toLowerCase();
  if (q.includes('flight') || q.includes('plane') || q.includes('air') || q.includes('airline') || q.includes('aviation') || q.includes('domestic flight') || q.includes('international flight')) return 'flight';
  if (q.includes('taxi') || q.includes('cab')) return 'taxi';
  if (q.includes('ride') || q.includes('uber') || q.includes('grab') || q.includes('didi') || q.includes('lyft') || q.includes('ola') || q.includes('gojek')) return 'rideshare';
  if (q.includes('light rail') || q.includes('g:link') || q.includes('glink')) return 'light_rail';
  if (q.includes('tram') || q.includes('streetcar') || q.includes('trolley')) return 'tram';
  if (q.includes('metro') || q.includes('subway') || q.includes('mrt') || q.includes('underground')) return 'metro';
  if (q.includes('train') || q.includes('rail') || q.includes('railway') || q.includes('shinkansen') || q.includes('bullet')) return 'train';
  if (q.includes('bus') || q.includes('coach')) return 'bus';
  if (q.includes('ferry') || q.includes('boat') || q.includes('water') || q.includes('cruise') || q.includes('catamaran')) return 'ferry';
  if (q.includes('bicycle') || q.includes('bike') || q.includes('cycling') || q.includes('e-bike')) return 'bicycle';
  if (q.includes('car') || q.includes('rental') || q.includes('drive') || q.includes('sedan')) return 'car';
  if (q.includes('card') || q.includes('opal') || q.includes('oyster') || q.includes('go card') || q.includes('suica') || q.includes('pasmo') || q.includes('ticket') || q.includes('pass') || q.includes('ez-link') || q.includes('octopus') || q.includes('touch n go')) return 'card';
  if (q.includes('walk') || q.includes('foot') || q.includes('pedestrian') || q.includes('hiking')) return 'walking';
  if (q.includes('shuttle') || q.includes('airport') || q.includes('transfer')) return 'shuttle';
  if (q.includes('cable') || q.includes('gondola') || q.includes('funicular') || q.includes('chairlift')) return 'cable_car';
  if (q.includes('scooter') || q.includes('motorbike') || q.includes('motorcycle') || q.includes('vespa') || q.includes('moped') || q.includes('tuk tuk') || q.includes('tuktuk') || q.includes('rickshaw')) return 'scooter';
  return null;
};

// Detect airport/departure activities
const isAirportActivity = (query) => {
  if (!query) return false;
  const q = query.toLowerCase();
  const airportKeywords = ['airport', 'depart', 'departure', 'terminal', 'check-in counter',
    'boarding', 'flight out', 'fly out', 'fly to', 'fly from', 'airline check'];
  return airportKeywords.some(k => q.includes(k));
};

// ==================== GOOGLE PLACES API (PRIMARY) ====================

// Fetch a single image from Google Places via backend proxy
const fetchGooglePlacesImage = async (query, signal) => {
  if (!query || query.length < 2) return null;
  try {
    const res = await fetch(
      `${API_BASE}/places/photos?query=${encodeURIComponent(query.trim())}&count=1`,
      { signal }
    );
    if (!res.ok) return null;
    const data = await res.json();
    if (data.success && data.photos?.length > 0) {
      return data.photos[0].url;
    }
  } catch (e) {
    if (e.name === 'AbortError') throw e;
  }
  return null;
};

// Fetch multiple images from Google Places for gallery
const fetchGooglePlacesGallery = async (query, count = 6) => {
  if (!query || query.length < 2) return [];
  try {
    const res = await fetch(
      `${API_BASE}/places/photos?query=${encodeURIComponent(query.trim())}&count=${count}`
    );
    if (!res.ok) return [];
    const data = await res.json();
    if (data.success && data.photos?.length > 0) {
      return data.photos.map(p => ({
        url: p.url,
        title: data.place_name || query,
        description: p.author ? `Photo by ${p.author}` : '',
      }));
    }
  } catch { /* silent */ }
  return [];
};

// ==================== PEXELS API (SECONDARY) ====================

// Fetch a single image from Pexels via backend proxy
const fetchPexelsImage = async (query, signal) => {
  if (!query || query.length < 2) return null;
  try {
    const res = await fetch(
      `${API_BASE}/images/search?query=${encodeURIComponent(query.trim())}&per_page=1`,
      { signal }
    );
    if (!res.ok) return null;
    const data = await res.json();
    if (data.success && data.photos?.length > 0) {
      return data.photos[0].url || data.photos[0].medium;
    }
  } catch (e) {
    if (e.name === 'AbortError') throw e;
  }
  return null;
};

// Fetch multiple images from Pexels for gallery
const fetchPexelsGallery = async (query, count = 6) => {
  if (!query || query.length < 2) return [];
  try {
    const res = await fetch(
      `${API_BASE}/images/search?query=${encodeURIComponent(query.trim())}&per_page=${count}`
    );
    if (!res.ok) return [];
    const data = await res.json();
    if (data.success && data.photos?.length > 0) {
      return data.photos.map(p => ({
        url: p.url || p.medium,
        title: p.alt || query,
        description: p.photographer ? `Photo by ${p.photographer}` : '',
      }));
    }
  } catch { /* silent */ }
  return [];
};

// ==================== WIKIPEDIA API (FALLBACK) ====================

// Check if image URL is a map or diagram
const isMapOrDiagramUrl = (url) => {
  if (!url) return false;
  const u = url.toLowerCase();
  const mapPatterns = ['map', 'diagram', 'chart', 'flag', 'coat_of_arms', 'seal_of',
    'logo', 'icon', 'symbol', 'emblem', 'svg', 'locator', 'location_dot', 'blank_map',
    'administrative', 'political', 'topographic', 'bathymetric', 'relief_map',
    'road_map', 'satellite_image', 'orthophoto', 'openstreetmap', 'wikimedia_maps'];
  return mapPatterns.some(p => u.includes(p));
};

// Check if image URL looks like a portrait/person photo
const isPortraitImageUrl = (url) => {
  if (!url) return false;
  const u = url.toLowerCase();
  const portraitPatterns = [
    'portrait', 'headshot', 'mugshot', 'face_', 'photo_of_',
    'at_the_', 'at_event', 'red_carpet', 'press_conference',
    'interview', 'signing', 'ceremony', 'award', 'speech',
    'official_photo', 'profile', 'closeup', 'close-up',
  ];
  return portraitPatterns.some(p => u.includes(p));
};

// Check if a Wikipedia page is irrelevant
const isIrrelevantPage = (description, type) => {
  if (!description) return false;
  const d = description.toLowerCase();
  const personWords = ['singer', 'actor', 'actress', 'politician', 'footballer', 'player',
    'musician', 'writer', 'author', 'director', 'comedian', 'chef', 'model',
    'born', 'died', 'is a malaysian', 'is an american', 'is a british', 'is an indian',
    'is a japanese', 'is an australian', 'is a canadian', 'is a chinese', 'is a south korean',
    'is a filipino', 'is a thai', 'is an indonesian', 'is a singaporean', 'is a french',
    'is a german', 'is an italian', 'is a spanish',
    'businessman', 'businesswoman', 'entrepreneur', 'ceo', 'minister', 'president',
    'prince', 'princess', 'king', 'queen', 'duke', 'celebrity', 'influencer',
    'sportsperson', 'athlete', 'boxer', 'swimmer', 'dancer', 'rapper', 'dj',
    'founder', 'co-founder', 'chairman', 'philanthropist', 'he is', 'she is',
    'he was', 'she was', 'his career', 'her career'];
  const animalWords = ['species', 'bird', 'mammal', 'reptile', 'fish', 'insect',
    'genus', 'family of', 'animal', 'butterfly', 'moth', 'beetle', 'snake',
    'lizard', 'frog', 'toad', 'parrot', 'eagle', 'hawk', 'owl', 'ibis',
    'heron', 'crane', 'duck', 'goose', 'penguin', 'flamingo', 'sparrow',
    'endemic to', 'found in', 'native to'];

  // Always filter persons and animals for all types
  if (personWords.some(w => d.includes(w))) return true;
  if (animalWords.some(w => d.includes(w))) return true;

  const mediaWords = ['film', 'movie', 'television', 'tv series', 'anime', 'novel', 'album', 'song', 'video game'];
  if (mediaWords.some(w => d.includes(w))) return true;

  return false;
};

// Validate image URL is acceptable
const isAcceptableImage = (url) => {
  if (!url) return false;
  if (isMapOrDiagramUrl(url)) return false;
  if (isPortraitImageUrl(url)) return false;
  return true;
};

// Try getting a good image from a Wikipedia page summary
const tryGetImageFromSummary = async (pageTitle, signal) => {
  try {
    const res = await fetch(
      `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(pageTitle.replace(/\s+/g, '_'))}`,
      { signal }
    );
    if (!res.ok) return null;
    const data = await res.json();
    const desc = data.description || data.extract || '';
    if (isIrrelevantPage(desc)) return null;
    const imgUrl = data.thumbnail?.source || data.originalimage?.source;
    if (imgUrl && isAcceptableImage(imgUrl)) return imgUrl.replace(/\/\d+px-/, '/600px-');
  } catch (e) { if (e.name === 'AbortError') throw e; }
  return null;
};

// Fetch Wikipedia image with filtering (fallback)
const fetchWikiImageWithFilter = async (query, signal) => {
  if (!query || query.length < 2) return null;

  // Direct page lookup
  const directQuery = query.replace(/[()]/g, '').replace(/\s+/g, '_').trim();
  const img1 = await tryGetImageFromSummary(directQuery, signal);
  if (img1) return img1;

  // Search API
  const searchQuery = query.replace(/[()]/g, '').trim();
  try {
    const searchRes = await fetch(
      `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(searchQuery)}&srnamespace=0&srlimit=5&format=json&origin=*`,
      { signal }
    );
    if (searchRes.ok) {
      const searchData = await searchRes.json();
      for (const result of (searchData.query?.search || [])) {
        const img = await tryGetImageFromSummary(result.title, signal);
        if (img) return img;
      }
    }
  } catch (e) { if (e.name === 'AbortError') throw e; }

  return null;
};

// Fetch multiple Wikipedia images (fallback for gallery)
const fetchWikiGallery = async (query, count = 6) => {
  if (!query || query.length < 2) return [];
  const images = [];
  const seen = new Set();
  const searchQuery = query.replace(/[()]/g, '').trim();

  try {
    const pageTitle = searchQuery.replace(/\s+/g, '_');
    const mediaRes = await fetch(
      `https://en.wikipedia.org/w/api.php?action=query&titles=${encodeURIComponent(pageTitle)}&prop=images&imlimit=20&format=json&origin=*`
    );
    if (mediaRes.ok) {
      const mediaData = await mediaRes.json();
      const page = Object.values(mediaData.query?.pages || {})[0];
      if (page?.images) {
        const imageFiles = page.images
          .map(img => img.title)
          .filter(t => /\.(jpg|jpeg|png|webp)$/i.test(t))
          .filter(t => !isMapOrDiagramUrl(t.toLowerCase()));
        for (const file of imageFiles.slice(0, 10)) {
          if (images.length >= count) break;
          try {
            const infoRes = await fetch(
              `https://en.wikipedia.org/w/api.php?action=query&titles=${encodeURIComponent(file)}&prop=imageinfo&iiprop=url&iiurlwidth=800&format=json&origin=*`
            );
            if (infoRes.ok) {
              const infoData = await infoRes.json();
              const infoPage = Object.values(infoData.query?.pages || {})[0];
              const url = infoPage?.imageinfo?.[0]?.thumburl || infoPage?.imageinfo?.[0]?.url;
              if (url && isAcceptableImage(url) && !seen.has(url)) {
                seen.add(url);
                images.push({ url, title: file.replace('File:', '').replace(/_/g, ' ') });
              }
            }
          } catch { continue; }
        }
      }
    }
  } catch { /* silent */ }

  if (images.length < count) {
    try {
      const searchRes = await fetch(
        `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(searchQuery)}&srnamespace=0&srlimit=${count + 5}&format=json&origin=*`
      );
      if (searchRes.ok) {
        const searchData = await searchRes.json();
        for (const result of (searchData.query?.search || [])) {
          if (images.length >= count) break;
          try {
            const summaryRes = await fetch(
              `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(result.title.replace(/\s+/g, '_'))}`
            );
            if (summaryRes.ok) {
              const data = await summaryRes.json();
              const desc = data.description || data.extract || '';
              if (isIrrelevantPage(desc)) continue;
              const imgUrl = data.originalimage?.source || data.thumbnail?.source;
              if (imgUrl && isAcceptableImage(imgUrl) && !seen.has(imgUrl)) {
                seen.add(imgUrl);
                images.push({
                  url: imgUrl.replace(/\/\d+px-/, '/800px-'),
                  title: result.title,
                  description: (data.extract || '').substring(0, 120),
                });
              }
            }
          } catch { continue; }
        }
      }
    } catch { /* silent */ }
  }

  return images.slice(0, count);
};

// ==================== GRADIENT FALLBACK ====================

const queryToGradient = (query) => {
  let hash = 0;
  for (let i = 0; i < (query || '').length; i++) {
    hash = ((hash << 5) - hash) + query.charCodeAt(i);
    hash |= 0;
  }
  const hue1 = Math.abs(hash % 360);
  const hue2 = (hue1 + 40) % 360;
  return `linear-gradient(135deg, hsl(${hue1}, 45%, 65%), hsl(${hue2}, 55%, 50%))`;
};

// ==================== MAIN COMPONENT ====================

const LocationImage = ({ query, type = 'activity', index = 0, className = '', alt = '' }) => {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!query) { setLoading(false); setFailed(true); return; }

    // All image types go through the standard fetch chain: Google Places → Pexels → Wikipedia → Stock
    const cacheKey = `img_${type}_${query.replace(/\s+/g, '_').toLowerCase().trim()}`;

    // Check in-memory cache
    if (imageCache[cacheKey]) {
      setSrc(imageCache[cacheKey]);
      setLoading(false);
      return;
    }

    // Check localStorage cache
    const cached = localStorage.getItem(cacheKey);
    if (cached && cached !== 'null' && cached !== 'failed') {
      setSrc(cached);
      imageCache[cacheKey] = cached;
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setSrc(null);
    setFailed(false);

    const doFetch = async () => {
      // Step 1: Try Google Places (primary — real photos of exact places)
      try {
        const placesUrl = await fetchGooglePlacesImage(query, controller.signal);
        if (placesUrl) {
          setSrc(placesUrl);
          imageCache[cacheKey] = placesUrl;
          localStorage.setItem(cacheKey, placesUrl);
          setLoading(false);
          return;
        }
      } catch (e) {
        if (e.name === 'AbortError') return;
      }

      // Step 2: Try Pexels (secondary — high quality stock)
      try {
        const pexelsUrl = await fetchPexelsImage(query, controller.signal);
        if (pexelsUrl) {
          setSrc(pexelsUrl);
          imageCache[cacheKey] = pexelsUrl;
          localStorage.setItem(cacheKey, pexelsUrl);
          setLoading(false);
          return;
        }
      } catch (e) {
        if (e.name === 'AbortError') return;
      }

      // Step 3: Try Wikipedia (fallback)
      try {
        const wikiUrl = await fetchWikiImageWithFilter(query, controller.signal);
        if (wikiUrl) {
          setSrc(wikiUrl);
          imageCache[cacheKey] = wikiUrl;
          localStorage.setItem(cacheKey, wikiUrl);
          setLoading(false);
          return;
        }
      } catch (e) {
        if (e.name === 'AbortError') return;
      }

      // Step 4: Transport stock fallback
      if (type === 'transport') {
        const category = getTransportCategory(query);
        const images = (category && TRANSPORT_STOCK_IMAGES[category]) || TRANSPORT_STOCK_IMAGES.bus;
        const stockImg = images[index % images.length];
        setSrc(stockImg);
        imageCache[cacheKey] = stockImg;
        localStorage.setItem(cacheKey, stockImg);
      } else {
        setFailed(true);
        localStorage.setItem(cacheKey, 'failed');
      }
      setLoading(false);
    };

    doFetch();
    return () => controller.abort();
  }, [query, type, index]);

  if (loading || (!src && !failed)) {
    return (
      <div
        className={className}
        style={{ background: queryToGradient(query), display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}
      >
        <Loader2 className="w-6 h-6 text-white/70 animate-spin mb-2" />
        <span className="text-white/80 text-xs font-medium px-4 text-center leading-tight max-w-[80%] truncate">
          {alt || query || 'Loading...'}
        </span>
      </div>
    );
  }

  if (src) {
    return (
      <img
        src={src}
        alt={alt || query || 'Travel image'}
        className={className}
        onError={() => {
          setSrc(null);
          setFailed(true);
        }}
      />
    );
  }

  return (
    <div
      className={className}
      style={{ background: queryToGradient(query), display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-start', padding: '1rem' }}
    >
      <span className="text-white font-serif text-lg drop-shadow-lg leading-tight max-w-full">
        {alt || query || ''}
      </span>
    </div>
  );
};

export default LocationImage;

// Fetch a city image (for generation screen) — Google Places first, then Pexels, then Wikipedia
export const fetchCityWikiImage = async (cityName) => {
  if (!cityName) return null;
  // Try Google Places
  try {
    const res = await fetch(
      `${API_BASE}/places/photos?query=${encodeURIComponent(cityName + ' city')}&count=1`
    );
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.photos?.length > 0) {
        return data.photos[0].url;
      }
    }
  } catch (e) { /* Google Places failed */ }
  // Try Pexels
  try {
    const res = await fetch(
      `${API_BASE}/images/search?query=${encodeURIComponent(cityName + ' city skyline landmark')}&per_page=1`
    );
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.photos?.length > 0) {
        return data.photos[0].url || data.photos[0].medium;
      }
    }
  } catch (e) { /* Pexels failed */ }
  // Fallback to Wikipedia
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    const res = await fetch(
      `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(cityName.replace(/\s+/g, '_'))}`,
      { signal: controller.signal }
    );
    clearTimeout(timeout);
    if (res.ok) {
      const data = await res.json();
      const imgUrl = data.originalimage?.source || data.thumbnail?.source;
      if (imgUrl && !isMapOrDiagramUrl(imgUrl)) return imgUrl.replace(/\/\d+px-/, '/1200px-');
    }
  } catch (e) { /* timeout or fetch error */ }
  return null;
};

// Fetch multiple images for gallery — Google Places first, then Pexels, then Wikipedia
export const fetchMultipleWikiImages = async (query, count = 6) => {
  if (!query || query.length < 2) return [];

  // Try Google Places first (real visitor photos)
  const placesImages = await fetchGooglePlacesGallery(query, count);
  if (placesImages.length >= 3) return placesImages.slice(0, count);

  // Supplement with Pexels
  const pexelsImages = await fetchPexelsGallery(query, count - placesImages.length);
  const combined = [...placesImages, ...pexelsImages];
  if (combined.length >= 3) return combined.slice(0, count);

  // Last resort: supplement with Wikipedia
  const wikiImages = await fetchWikiGallery(query, count - combined.length);
  return [...combined, ...wikiImages].slice(0, count);
};
